import { Component, inject, OnInit } from '@angular/core';
import { DecimalPipe, NgComponentOutlet } from '@angular/common';
import { ApiResponse, API_STATUS } from '../../../model/api.model';
import { surveyMetricsApiConfig } from '../../../model/survey.model';
import { CommonService } from '../../../services/common.service';
import { LoadingSkeletonComponent } from '../../../layout/loading-skeleton/loading-skeleton.component';
import { SurveyComponent } from '../survey.component';

export interface SurveyHeaderCard {
  surveyId: string;
  eventName: string;
  status: 'Active' | 'Upcoming' | 'Completed';
  startDate: string;
  totalRegistrations: number | null;
  surveyCompletionPercentage: string;
}

@Component({
  selector: 'app-pas-dashboard',
  standalone: true,
  imports: [DecimalPipe, NgComponentOutlet, LoadingSkeletonComponent],
  templateUrl: './pas-dashboard.component.html',
  styleUrl: './pas-dashboard.component.scss'
})
export class PasDashboardComponent implements OnInit {
  private readonly commonService = inject(CommonService);
  private readonly allStatuses: Array<SurveyHeaderCard['status']> = ['Active', 'Upcoming', 'Completed'];

  readonly title = 'Survey Dashboard';
  private readonly surveyIdentity = {
    surveyId: 'WATT-THE-TECH',
    eventName: '2027 WATT THE TECH',
    status: 'Active' as SurveyHeaderCard['status'],
    startDate: '10/01/2026',
  };
  headerCards: SurveyHeaderCard[] = [{ ...this.surveyIdentity, totalRegistrations: null, surveyCompletionPercentage: '' }];
  isLoading = false;

  selectedFilters: Array<SurveyHeaderCard['status']> = ['Active'];
  searchTerm = '';
  showSurveyDetails = false;
  readonly surveyComponent = SurveyComponent;

  ngOnInit(): void {
    this.fetchSurveySummary();
  }

  private fetchSurveySummary(): void {
    this.isLoading = true;
    this.commonService.getDataFromDataSource(surveyMetricsApiConfig, surveyMetricsApiConfig.urlPath ?? '')
      .then((response: ApiResponse) => {
        if (response.status === API_STATUS['Success']) {
          this.headerCards = this.mapSurveySummaryToCards(response);
        }
      })
      .catch((error: Error) => {
        console.info('Error fetching survey summary:', error); 
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private mapSurveySummaryToCards(response: ApiResponse): SurveyHeaderCard[] {
    const extendedResponse = response as ApiResponse & { payload?: unknown; result?: unknown };
    const source = extendedResponse.payload ?? extendedResponse.result;
    const rows = Array.isArray(source) ? source : (source ? [source] : []);
    return rows.map((item: any) => this.mapSurveySummaryItem(item));
  }

  private mapSurveySummaryItem(item: any): SurveyHeaderCard {
    return {
      ...this.surveyIdentity,
      totalRegistrations: Number(item?.total_registrants) || 0,
      surveyCompletionPercentage: String(item?.responses_collected ?? ''),
    };
  }

  get filteredCards(): SurveyHeaderCard[] {
    const normalizedSearch = this.searchTerm.trim().toLowerCase();
    return this.headerCards.filter(card => {
      const matchesStatus = this.selectedFilters.includes(card.status);
      const matchesSearch = !normalizedSearch || card.eventName.toLowerCase().includes(normalizedSearch);
      return matchesStatus && matchesSearch;
    });
  }

  get displayedCount(): number {
    return this.filteredCards.length;
  }

  get totalCount(): number {
    return this.headerCards.length;
  }

  toggleFilter(filter: SurveyHeaderCard['status']): void {
    if (this.selectedFilters.includes(filter)) {
      this.selectedFilters = this.selectedFilters.filter(current => current !== filter);
      return;
    }
    const next = new Set([...this.selectedFilters, filter]);
    this.selectedFilters = this.allStatuses.filter(status => next.has(status));
  }

  isFilterSelected(filter: SurveyHeaderCard['status']): boolean {
    return this.selectedFilters.includes(filter);
  }

  onSearchChange(value: string): void {
    this.searchTerm = value;
  }

  openSurvey(_card: SurveyHeaderCard): void {
    this.showSurveyDetails = true;
  }

  backToDashboard(): void {
    this.showSurveyDetails = false;
  }

  get surveyInputs() {
    return {
      backFuncCall: this.backToDashboard.bind(this)
    };
  }
}
